import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { employerService } from '../services/employerService';
import { entitySyncService } from '../services/entitySyncService';

// --- Core Async Thunks ---
export const fetchEmployers = createAsyncThunk('employers/fetchEmployers', async (params, { rejectWithValue }) => {
  try {
    const response = await employerService.fetchEmployers(params);
    return response.data;
  } catch (error) { return rejectWithValue(error.response?.data?.message || error.message); }
});

export const loadEmployersFromCache = createAsyncThunk('employers/loadFromCache', async (_, { rejectWithValue }) => {
  try {
    const data = await entitySyncService.getLocalEntities('employers');
    return data;
  } catch (error) { return rejectWithValue(error.message); }
});

export const fetchEmployerById = createAsyncThunk('employers/fetchEmployerById', async (payload, { rejectWithValue }) => {
  const employerId = typeof payload === 'object' ? payload.id : payload;
  try {
    const response = await employerService.getEmployerById(employerId);
    return response.data ? response.data : response;
  } catch (error) { return rejectWithValue(error.response?.data?.message || error.message); }
}, {
  condition: (payload, { getState }) => {
    if (payload?.forceRefresh) return true; // Always fetch if forced
    const employerId = typeof payload === 'object' ? payload.id : payload;
    const { employers } = getState();
    if (employers.loading || (employers.selectedEmployer?.id === employerId && !employers.error)) {
      return false;
    }
  }
});

export const createEmployer = createAsyncThunk('employers/createEmployer', async (employerData, { rejectWithValue }) => {
  try {
    const response = await employerService.createEmployer(employerData);
    return response.data;
  } catch (error) { return rejectWithValue(error.response?.data?.message || error.message); }
});

export const updateEmployer = createAsyncThunk('employers/updateEmployer', async ({ employerId, updates }, { rejectWithValue }) => {
  try {
    const response = await employerService.updateEmployer(employerId, updates);
    return response.data;
  } catch (error) { return rejectWithValue(error.response?.data?.message || error.message); }
});

export const deleteEmployer = createAsyncThunk('employers/deleteEmployer', async (employerId, { rejectWithValue }) => {
  try {
    await employerService.deleteEmployer(employerId);
    return employerId;
  } catch (error) { return rejectWithValue(error.response?.data?.message || error.message); }
});

// --- Modal-Specific Thunks ---
export const searchEmployers = createAsyncThunk('employers/searchEmployers', async (params, { rejectWithValue }) => {
  try {
    const response = await employerService.fetchEmployers(params);
    return response.data;
  } catch (error) { return rejectWithValue(error.response?.data?.message || error.message); }
});

export const fetchContactsForSelection = createAsyncThunk('employers/fetchContactsForSelection', async (filters, { rejectWithValue }) => {
  try {
    const response = await employerService.fetchEmployers(filters);
    return response.data;
  } catch (error) { return rejectWithValue(error.response?.data?.message || error.message); }
});

const employerSlice = createSlice({
  name: 'employers',
  initialState: {
    employers: [],
    selectedEmployer: null,
    loading: false,
    error: null,
    pagination: null,

    // State for search results in AddEmployerOrContactModal
    searchResults: [],
    searchLoading: false,
    searchError: null,

    // State for the EmployerSelectionModal contact list
    contactList: [],
    contactListLoading: false,
    contactListError: null,
    contactPagination: null,

    // State for CUD operations
    actionLoading: false,
    actionError: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.actionError = null;
      state.searchError = null;
      state.contactListError = null;
    },
    clearSearchResults: (state) => { state.searchResults = []; },
    clearSelectedEmployer: (state) => { state.selectedEmployer = null; },
    clearContactList: (state) => {
      state.contactList = [];
      state.contactPagination = null;
      state.contactListError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Main list fetching
      .addCase(fetchEmployers.pending, (state) => { state.loading = true; })
      .addCase(fetchEmployers.fulfilled, (state, action) => {
        state.loading = false;
        state.employers = action.payload.data || [];
        state.pagination = action.payload.pagination || null;
      })
      .addCase(fetchEmployers.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(loadEmployersFromCache.fulfilled, (state, action) => {
        state.employers = action.payload;
      })

      // Fetch by ID
      .addCase(fetchEmployerById.pending, (state) => { state.loading = true; })
      .addCase(fetchEmployerById.fulfilled, (state, action) => { state.loading = false; state.selectedEmployer = action.payload; })
      .addCase(fetchEmployerById.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      // Modal company search
      .addCase(searchEmployers.pending, (state) => { state.searchLoading = true; })
      .addCase(searchEmployers.fulfilled, (state, action) => {
        state.searchLoading = false;
        state.searchResults = action.payload.data || [];
      })
      .addCase(searchEmployers.rejected, (state, action) => { state.searchLoading = false; state.searchError = action.payload; })

      // Contact list for selection modal
      .addCase(fetchContactsForSelection.pending, (state) => { state.contactListLoading = true; })
      .addCase(fetchContactsForSelection.fulfilled, (state, action) => {
        state.contactListLoading = false;
        // Flatten contacts, adding a reference to the parent employer
        const contacts = (action.payload.data || []).flatMap(employer =>
          (employer.contacts || []).map(contact => ({
            ...contact,
            employer: { id: employer.id, name: employer.name }
          }))
        );
        state.contactList = contacts;
        state.contactPagination = action.payload.pagination || null;
      })
      .addCase(fetchContactsForSelection.rejected, (state, action) => { state.contactListLoading = false; state.contactListError = action.payload; })

      // Generic handlers for CUD actions
      .addMatcher(
        (action) => [createEmployer.pending, updateEmployer.pending, deleteEmployer.pending].includes(action.type),
        (state) => { state.actionLoading = true; state.actionError = null; }
      )
      .addMatcher(
        (action) => [createEmployer.fulfilled, updateEmployer.fulfilled, deleteEmployer.fulfilled].includes(action.type),
        (state, action) => {
          state.actionLoading = false;
          if (action.type === updateEmployer.fulfilled.type && state.selectedEmployer?.id === action.payload.id) {
            state.selectedEmployer = action.payload;
          }

          // Mirror to IndexedDB
          if (action.type === createEmployer.fulfilled.type || action.type === updateEmployer.fulfilled.type) {
            entitySyncService.upsertLocalEntity('employers', action.payload);
          } else if (action.type === deleteEmployer.fulfilled.type) {
            entitySyncService.deleteLocalEntity('employers', action.payload);
          }
        }
      )
      .addMatcher(
        (action) => [createEmployer.rejected, updateEmployer.rejected, deleteEmployer.rejected].includes(action.type),
        (state, action) => { state.actionLoading = false; state.actionError = action.payload; }
      );
  },
});

export const { clearError, clearSearchResults, clearSelectedEmployer, clearContactList } = employerSlice.actions;
export default employerSlice.reducer;
